import type { Session } from "@supabase/supabase-js";
import { type FormEvent, type ReactNode, useEffect, useMemo, useState } from "react";
import { AdminAccess } from "./AdminAccess";
import { getAdminSupabaseClient, readAdminSession, signOutAdmin } from "./lib/adminRepository";
import {
  loadWorkspaceSnapshot,
  markPersonalBookingPaid,
  markPersonalBookingUnpaid,
  saveWorkspaceHourlyPricing,
  saveWorkspaceOffer,
  type PersonalBooking,
  type WorkspaceOffer,
  type WorkspaceSnapshot,
  type WorkspaceVariant,
} from "./lib/adminWorkspaceRepository";
import "./admin-workspace.css";
import "./hourly-pricing-admin.css";

const DURATION_PRESETS = [30, 45, 60, 75, 90, 120, 180, 240];

export function AdminWorkspaceAccess() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    readAdminSession()
      .then((value) => active && setSession(value))
      .finally(() => active && setLoading(false));
    const { data } = getAdminSupabaseClient().auth.onAuthStateChange((_event, nextSession) => {
      if (active) {
        setSession(nextSession);
        setLoading(false);
      }
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  if (loading) return <div className="workspace-loading">Verwaltung wird geöffnet …</div>;
  if (!session) return <AdminAccess />;
  return <AdminWorkspace />;
}

type Section = "bookings" | "offers" | "locations" | "availability" | "history";

function AdminWorkspace() {
  const personSlug = requestedAdminPerson();
  const personName = personSlug === "katrin" ? "Katrin" : "Rayk";
  const [snapshot, setSnapshot] = useState<WorkspaceSnapshot | null>(null);
  const [section, setSection] = useState<Section>("bookings");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    setBusy(true);
    setError(null);
    try {
      setSnapshot(await loadWorkspaceSnapshot());
    } catch (reason) {
      setError(toMessage(reason));
    } finally {
      setBusy(false);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: initial workspace load intentionally runs once.
  useEffect(() => {
    void refresh();
  }, []);

  async function run(action: () => Promise<unknown>, success: string) {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      await action();
      setMessage(success);
      setSnapshot(await loadWorkspaceSnapshot());
    } catch (reason) {
      setError(toMessage(reason));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="workspace-shell">
      <header className="workspace-header">
        <div>
          <p className="workspace-kicker">Booking Verwaltung</p>
          <h1>{personName} · 1:1</h1>
          <p className="workspace-lead">
            Buchungen, Zahlungen, Angebote und Zeiten an einem Ort.
          </p>
        </div>
        <div className="workspace-header-actions">
          <button type="button" onClick={() => void refresh()} disabled={busy}>
            Aktualisieren
          </button>
          <button type="button" onClick={() => void signOutAdmin()}>
            Abmelden
          </button>
        </div>
      </header>

      <nav className="workspace-tabs" aria-label="Verwaltungsbereiche">
        <Tab
          active={section === "bookings"}
          onClick={() => setSection("bookings")}
          label="Buchungen & Umsatz"
        />
        <Tab active={section === "offers"} onClick={() => setSection("offers")} label="Angebote" />
        <Tab
          active={section === "locations"}
          onClick={() => setSection("locations")}
          label="Orte"
        />
        <Tab
          active={section === "availability"}
          onClick={() => setSection("availability")}
          label="Zeiten"
        />
        <Tab active={section === "history"} onClick={() => setSection("history")} label="Verlauf" />
      </nav>

      {message ? <div className="workspace-notice success">{message}</div> : null}
      {error ? <div className="workspace-notice error">{error}</div> : null}

      {!snapshot ? (
        <section className="workspace-panel">
          <p>{busy ? "Daten werden geladen …" : "Keine Daten verfügbar."}</p>
        </section>
      ) : null}
      {snapshot && section === "bookings" ? (
        <PersonalBookingsOverview
          bookings={snapshot.bookings}
          personSlug={personSlug}
          busy={busy}
          onChanged={refresh}
        />
      ) : null}
      {snapshot && section === "offers" ? (
        <OfferWorkspace snapshot={snapshot} busy={busy} run={run} />
      ) : null}
      {snapshot && section === "locations" ? <LocationOverview snapshot={snapshot} /> : null}
      {snapshot && section === "availability" ? <AvailabilityOverview snapshot={snapshot} /> : null}
      {snapshot && section === "history" ? <HistoryOverview snapshot={snapshot} /> : null}
    </main>
  );
}

function requestedAdminPerson(): "rayk" | "katrin" {
  const requested = new URLSearchParams(window.location.search).get("person");
  if (requested === "katrin") return "katrin";
  if (requested === "rayk") return "rayk";
  return window.location.hostname.toLowerCase() === "book.katrinschoerling.com" ? "katrin" : "rayk";
}

function PersonalBookingsOverview({
  bookings,
  personSlug,
  busy,
  onChanged,
}: {
  bookings: PersonalBooking[];
  personSlug: "rayk" | "katrin";
  busy: boolean;
  onChanged: () => Promise<void>;
}) {
  const personName = personSlug === "katrin" ? "Katrin" : "Rayk";
  const own = bookings
    .filter((booking) => booking.resourceSlug === personSlug)
    .sort((left, right) => Date.parse(right.startsAt || right.createdAt) - Date.parse(left.startsAt || left.createdAt));
  const now = Date.now();
  const active = own.filter((booking) => booking.status !== "cancelled");
  const upcoming = active
    .filter((booking) => Date.parse(booking.startsAt) >= now)
    .sort((left, right) => Date.parse(left.startsAt) - Date.parse(right.startsAt));
  const past = active
    .filter((booking) => Date.parse(booking.startsAt) < now)
    .sort((left, right) => Date.parse(right.startsAt) - Date.parse(left.startsAt));
  const paidRevenue = active
    .filter((booking) => booking.paymentStatus === "paid")
    .reduce((sum, booking) => sum + (booking.priceCents ?? 0), 0);
  const openRevenue = active
    .filter((booking) => ["unpaid", "pending"].includes(booking.paymentStatus))
    .reduce((sum, booking) => sum + (booking.priceCents ?? 0), 0);

  return (
    <section className="workspace-panel workspace-bookings">
      <div className="workspace-bookings__heading">
        <div>
          <p className="workspace-kicker">1:1 · {personName}</p>
          <h2>Buchungen & Zahlungen</h2>
          <p>Aktive und vergangene Termine – stornierte Buchungen werden hier nicht mehr geführt.</p>
        </div>
        <button
          className="workspace-quiet-action"
          type="button"
          onClick={() => downloadPersonalBookingsCsv(active, personName)}
          disabled={active.length === 0}
        >
          CSV exportieren
        </button>
      </div>

      <div className="workspace-booking-metrics">
        <div><strong>{upcoming.length}</strong><span>kommende Termine</span></div>
        <div><strong>{formatMoney(paidRevenue, "EUR")}</strong><span>bezahlt</span></div>
        <div><strong>{formatMoney(openRevenue, "EUR")}</strong><span>offen</span></div>
        <div><strong>{past.length}</strong><span>vergangene Termine</span></div>
      </div>

      <PersonalBookingGroup
        title="Kommende Termine"
        bookings={upcoming}
        empty="Keine kommenden Termine."
        busy={busy}
        onChanged={onChanged}
      />
      <PersonalBookingGroup
        title="Vergangene Termine"
        bookings={past}
        empty="Noch keine vergangenen Termine."
        busy={busy}
        onChanged={onChanged}
      />
    </section>
  );
}

function PersonalBookingGroup({
  title,
  bookings,
  empty,
  busy,
  onChanged,
}: {
  title: string;
  bookings: PersonalBooking[];
  empty: string;
  busy: boolean;
  onChanged: () => Promise<void>;
}) {
  return (
    <section className="workspace-booking-group">
      <h3>{title}</h3>
      {bookings.length === 0 ? (
        <p className="workspace-booking-empty">{empty}</p>
      ) : (
        <div className="workspace-booking-list">
          {bookings.map((booking) => (
            <PersonalBookingCard
              booking={booking}
              busy={busy}
              key={booking.id}
              onChanged={onChanged}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function PersonalBookingCard({
  booking,
  busy,
  onChanged,
}: {
  booking: PersonalBooking;
  busy: boolean;
  onChanged: () => Promise<void>;
}) {
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const name = [booking.firstName, booking.lastName].filter(Boolean).join(" ") || booking.email;

  async function reconcile(action: () => Promise<void>) {
    setPaymentBusy(true);
    setPaymentError(null);
    try {
      await action();
      await onChanged();
    } catch (reason) {
      setPaymentError(toMessage(reason));
    } finally {
      setPaymentBusy(false);
    }
  }

  return (
    <details className="workspace-booking-card">
      <summary>
        <span>
          <strong>{name}</strong>
          <small>
            {formatBookingDate(booking.startsAt)} · {booking.offerTitle || "1:1"} · {booking.durationMinutes ? formatDuration(booking.durationMinutes) : booking.variantLabel}
          </small>
        </span>
        <span className="workspace-booking-card__money">
          <strong>{formatMoney(booking.priceCents, booking.currency)}</strong>
          <small>{paymentStatusLabel(booking.paymentStatus)}</small>
        </span>
      </summary>
      <div className="workspace-booking-details">
        <div><span>Status</span><strong>{bookingStatusLabel(booking.status)}</strong></div>
        <div><span>Zahlung</span><strong>{paymentStatusLabel(booking.paymentStatus)}</strong></div>
        <div><span>Zahlungsweg</span><strong>{paymentChoiceLabel(booking.paymentChoice)}</strong></div>
        <div><span>Bezahlt am</span><strong>{booking.paidAt ? formatBookingDate(booking.paidAt) : "–"}</strong></div>
        <div><span>Ort</span><strong>{booking.locationName || "–"}</strong></div>
        <div><span>Referenz</span><strong>{booking.reference}</strong></div>
        <div><span>E-Mail</span><strong><a href={`mailto:${booking.email}`}>{booking.email}</a></strong></div>
        <div><span>Telefon</span><strong>{booking.phone ? <a href={`tel:${booking.phone}`}>{booking.phone}</a> : "–"}</strong></div>
      </div>
      {booking.note ? <p className="workspace-booking-note">{booking.note}</p> : null}
      {booking.status !== "cancelled" ? (
        <div className="workspace-booking-actions">
          {booking.paymentStatus !== "paid" ? (
            <button
              type="button"
              disabled={busy || paymentBusy}
              onClick={() => void reconcile(() => markPersonalBookingPaid(booking.id))}
            >
              {paymentBusy ? "Wird gespeichert …" : "Bezahlt markieren"}
            </button>
          ) : null}
          {booking.paymentStatus === "paid" && booking.paymentProvider !== "stripe" ? (
            <button
              type="button"
              disabled={busy || paymentBusy}
              onClick={() => void reconcile(() => markPersonalBookingUnpaid(booking.id))}
            >
              Wieder offen markieren
            </button>
          ) : null}
        </div>
      ) : null}
      {paymentError ? <p className="workspace-booking-error">{paymentError}</p> : null}
    </details>
  );
}

function downloadPersonalBookingsCsv(bookings: PersonalBooking[], personName: string) {
  const header = [
    "Vorname", "Nachname", "E-Mail", "Telefon", "Termin", "Angebot", "Dauer",
    "Status", "Zahlungsstatus", "Zahlungsweg", "Betrag", "Bezahlt am", "Referenz", "Notiz",
  ];
  const rows = bookings.map((booking) => [
    booking.firstName,
    booking.lastName,
    booking.email,
    booking.phone,
    booking.startsAt ? new Date(booking.startsAt).toLocaleString("de-DE") : "",
    booking.offerTitle,
    booking.durationMinutes ? String(booking.durationMinutes) : booking.variantLabel,
    bookingStatusLabel(booking.status),
    paymentStatusLabel(booking.paymentStatus),
    paymentChoiceLabel(booking.paymentChoice),
    booking.priceCents === null ? "" : String(booking.priceCents / 100),
    booking.paidAt ? new Date(booking.paidAt).toLocaleString("de-DE") : "",
    booking.reference,
    booking.note,
  ]);
  const csv = [header, ...rows]
    .map((row) => row.map((value) => `"${String(value ?? "").replaceAll('"', '""')}"`).join(";"))
    .join("\n");
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${personName.toLowerCase()}-1to1-buchungen.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function formatMoney(value: number | null, currency: string) {
  if (value === null) return "–";
  return new Intl.NumberFormat("de-DE", { style: "currency", currency }).format(value / 100);
}

function formatBookingDate(value: string) {
  if (!value) return "Termin offen";
  return new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function paymentStatusLabel(status: string) {
  if (status === "paid") return "Bezahlt";
  if (status === "pending") return "Zahlung läuft";
  if (status === "refunded") return "Erstattet";
  if (status === "failed") return "Fehlgeschlagen";
  if (status === "not_required") return "Keine Zahlung nötig";
  return "Offen";
}

function paymentChoiceLabel(choice: string) {
  if (choice === "online") return "Stripe";
  if (choice === "invoice") return "Rechnung";
  if (choice === "not_required") return "Keine Zahlung";
  return "–";
}

function bookingStatusLabel(status: string) {
  if (status === "confirmed") return "Bestätigt";
  if (status === "cancelled") return "Storniert";
  if (status === "completed") return "Abgeschlossen";
  if (status === "no_show") return "Nicht erschienen";
  return status || "Offen";
}

function Tab({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button className={active ? "is-active" : ""} type="button" onClick={onClick}>
      {label}
    </button>
  );
}

function OfferWorkspace({
  snapshot,
  busy,
  run,
}: {
  snapshot: WorkspaceSnapshot;
  busy: boolean;
  run: (action: () => Promise<unknown>, success: string) => Promise<void>;
}) {
  const [selectedId, setSelectedId] = useState("");
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [startOpenId, setStartOpenId] = useState("");
  const selected = snapshot.offers.find((offer) => offer.id === selectedId) ?? null;

  useEffect(() => {
    if (selectedId && !snapshot.offers.some((offer) => offer.id === selectedId)) {
      setSelectedId("");
    }
  }, [selectedId, snapshot.offers]);

  async function createOffer(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const title = newTitle.trim();
    if (!title) return;
    let createdId = "";
    const sortOrder = Math.max(0, ...snapshot.offers.map((offer) => offer.sortOrder)) + 10;
    await run(async () => {
      createdId = await saveWorkspaceOffer({
        title,
        eyebrow: "",
        description: "",
        setting: "",
        accent: "stone",
        sortOrder,
        isActive: false,
      });
    }, "Neues persönliches Angebot wurde als verborgener Entwurf angelegt.");
    setCreating(false);
    setNewTitle("");
    if (createdId) {
      setSelectedId(createdId);
      setStartOpenId(createdId);
    }
  }

  return (
    <section className="workspace-offer-area">
      <div className="workspace-action-row">
        <div>
          <p className="workspace-kicker">Persönliche Angebote</p>
          <h2>Auswählen oder neu anlegen</h2>
          <p>
            Die Seite bleibt ruhig. Erst nach deiner Auswahl öffnet sich genau der Bereich, den du
            gerade bearbeiten möchtest.
          </p>
        </div>
        <div className="workspace-action-buttons">
          <button
            className="workspace-primary"
            type="button"
            onClick={() => setCreating((current) => !current)}
          >
            {creating ? "Eingabe schließen" : "+ Neues persönliches Angebot"}
          </button>
          <a className="workspace-quiet-action" href="/?admin=events&new=1">
            + Neue Veranstaltung
          </a>
        </div>
      </div>

      {creating ? (
        <form className="workspace-create-card" onSubmit={createOffer}>
          <div>
            <p className="workspace-kicker">Schritt 1</p>
            <h3>Wie soll das neue Angebot heißen?</h3>
            <p>Es startet verborgen. Danach führst du Grunddaten, Preis und Orte einzeln durch.</p>
          </div>
          <label>
            Titel
            <input
              value={newTitle}
              onChange={(event) => setNewTitle(event.target.value)}
              placeholder="z. B. Intensives persönliches Gespräch"
              required
            />
          </label>
          <button className="workspace-primary" type="submit" disabled={busy}>
            Entwurf anlegen
          </button>
        </form>
      ) : null}

      <div className="workspace-grid">
        <aside className="workspace-offer-list" aria-label="Persönliche Angebote">
          <div className="workspace-list-heading">
            <strong>Vorhandene Angebote</strong>
            <span>{snapshot.offers.length}</span>
          </div>
          {snapshot.offers.map((offer) => (
            <button
              key={offer.id}
              type="button"
              className={offer.id === selected?.id ? "is-selected" : ""}
              onClick={() => {
                setSelectedId(offer.id);
                setStartOpenId("");
              }}
              aria-pressed={offer.id === selected?.id}
            >
              <strong>{offer.title}</strong>
              <span>
                {offer.isActive ? "Aktiv" : "Verborgen"} ·{" "}
                {offer.variants.filter((variant) => variant.isActive).length} Dauern
              </span>
            </button>
          ))}
        </aside>
        {selected ? (
          <OfferEditor
            key={selected.id}
            offer={selected}
            locations={snapshot.locations}
            links={snapshot.offerLocations}
            busy={busy}
            run={run}
            startOpen={selected.id === startOpenId}
          />
        ) : (
          <section className="workspace-empty-editor">
            <span aria-hidden="true">↗</span>
            <div>
              <p className="workspace-kicker">Bereit</p>
              <h2>Wähle ein Angebot</h2>
              <p>
                Dann erscheinen drei klare Schritte: Grunddaten, Dauer & Preis und Buchungsorte.
              </p>
            </div>
          </section>
        )}
      </div>
    </section>
  );
}

type OfferEditorSection = "basics" | "pricing" | "locations";

function OfferEditor({
  offer,
  locations,
  links,
  busy,
  run,
  startOpen,
}: {
  offer: WorkspaceOffer;
  locations: WorkspaceSnapshot["locations"];
  links: WorkspaceSnapshot["offerLocations"];
  busy: boolean;
  run: (action: () => Promise<unknown>, success: string) => Promise<void>;
  startOpen: boolean;
}) {
  const [draft, setDraft] = useState(offer);
  const [openSection, setOpenSection] = useState<OfferEditorSection | null>(
    startOpen ? "basics" : null,
  );
  const activeLocations = useMemo(
    () =>
      new Set(
        links
          .filter((link) => link.offerId === offer.id && link.isActive)
          .map((link) => link.locationId),
      ),
    [links, offer.id],
  );
  const activeDurations = offer.variants.filter((variant) => variant.isActive).length;

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await run(
      () =>
        saveWorkspaceOffer({
          id: draft.id,
          title: draft.title,
          eyebrow: draft.eyebrow,
          description: draft.description,
          setting: draft.setting,
          accent: draft.accent,
          sortOrder: draft.sortOrder,
          isActive: draft.isActive,
        }),
      "Angebot wurde gespeichert.",
    );
  }

  function toggle(section: OfferEditorSection) {
    setOpenSection((current) => (current === section ? null : section));
  }

  return (
    <div className="workspace-editor workspace-editor--guided">
      <div className="workspace-editor-summary">
        <div>
          <p className="workspace-kicker">Persönliches Angebot</p>
          <h2>{offer.title}</h2>
          <p>{offer.description || "Noch ohne Beschreibung."}</p>
        </div>
        <span className={offer.isActive ? "is-active" : ""}>
          {offer.isActive ? "Aktiv" : "Verborgener Entwurf"}
        </span>
      </div>

      <AccordionSection
        step="1"
        title="Grunddaten"
        summary="Titel, Beschreibung, Darstellung und Sichtbarkeit"
        open={openSection === "basics"}
        onToggle={() => toggle("basics")}
      >
        <form onSubmit={save} className="workspace-form">
          <label className="workspace-switch workspace-switch--standalone">
            <input
              type="checkbox"
              checked={draft.isActive}
              onChange={(event) => setDraft({ ...draft, isActive: event.target.checked })}
            />
            <span>{draft.isActive ? "Öffentlich aktiv" : "Als Entwurf verborgen"}</span>
          </label>
          <label>
            Titel
            <input
              value={draft.title}
              onChange={(event) => setDraft({ ...draft, title: event.target.value })}
              required
            />
          </label>
          <label>
            Kurzzeile
            <input
              value={draft.eyebrow ?? ""}
              onChange={(event) => setDraft({ ...draft, eyebrow: event.target.value })}
            />
          </label>
          <label>
            Beschreibung
            <textarea
              rows={4}
              value={draft.description ?? ""}
              onChange={(event) => setDraft({ ...draft, description: event.target.value })}
            />
          </label>
          <div className="workspace-two-columns">
            <label>
              Darstellung
              <select
                value={draft.accent}
                onChange={(event) =>
                  setDraft({
                    ...draft,
                    accent: event.target.value as WorkspaceOffer["accent"],
                  })
                }
              >
                <option value="stone">Stone</option>
                <option value="sage">Sage</option>
                <option value="navy">Navy</option>
                <option value="gold">Gold</option>
              </select>
            </label>
            <label>
              Reihenfolge
              <input
                type="number"
                value={draft.sortOrder}
                onChange={(event) => setDraft({ ...draft, sortOrder: Number(event.target.value) })}
              />
            </label>
          </div>
          <button className="workspace-primary" type="submit" disabled={busy}>
            Grunddaten speichern
          </button>
        </form>
      </AccordionSection>

      <AccordionSection
        step="2"
        title="Dauer & Preis"
        summary={
          activeDurations > 0
            ? `${activeDurations} buchbare Dauern · ein zentraler Stundensatz`
            : "Stundensatz festlegen und Dauern auswählen"
        }
        open={openSection === "pricing"}
        onToggle={() => toggle("pricing")}
      >
        <HourlyPricingEditor offer={offer} busy={busy} run={run} />
      </AccordionSection>

      <AccordionSection
        step="3"
        title="Buchungsorte"
        summary={`${activeLocations.size} Orte aktiv`}
        open={openSection === "locations"}
        onToggle={() => toggle("locations")}
      >
        <div className="workspace-chips">
          {locations.map((location) => (
            <span key={location.id} className={activeLocations.has(location.id) ? "is-active" : ""}>
              {location.mode === "online" ? "Online" : location.name}
            </span>
          ))}
        </div>
        <a className="workspace-secondary-link" href="/?admin=legacy">
          Orte und Zuordnung bearbeiten
        </a>
      </AccordionSection>
    </div>
  );
}

function AccordionSection({
  step,
  title,
  summary,
  open,
  onToggle,
  children,
}: {
  step: string;
  title: string;
  summary: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <section className={open ? "workspace-accordion is-open" : "workspace-accordion"}>
      <button
        className="workspace-accordion-trigger"
        type="button"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className="workspace-step-number">{step}</span>
        <span>
          <strong>{title}</strong>
          <small>{summary}</small>
        </span>
        <span className="workspace-accordion-symbol" aria-hidden="true">
          {open ? "−" : "+"}
        </span>
      </button>
      {open ? <div className="workspace-accordion-content">{children}</div> : null}
    </section>
  );
}

function HourlyPricingEditor({
  offer,
  busy,
  run,
}: {
  offer: WorkspaceOffer;
  busy: boolean;
  run: (action: () => Promise<unknown>, success: string) => Promise<void>;
}) {
  const activeVariants = useMemo(
    () =>
      offer.variants
        .filter((variant) => variant.isActive)
        .sort((left, right) => left.durationMinutes - right.durationMinutes),
    [offer.variants],
  );
  const initialRateCents = deriveHourlyRateCents(activeVariants);
  const initialDurations = uniqueDurations(
    activeVariants.map((variant) => variant.durationMinutes),
  );
  const initialDefault =
    activeVariants.find((variant) => variant.isDefault)?.durationMinutes ??
    initialDurations[0] ??
    60;
  const [priceMode, setPriceMode] = useState<"hourly" | "free">(
    activeVariants.some((variant) => variant.priceCents !== null) ? "hourly" : "free",
  );
  const [hourlyRate, setHourlyRate] = useState(
    initialRateCents === null ? "" : String(initialRateCents / 100),
  );
  const [selectedDurations, setSelectedDurations] = useState<number[]>(
    initialDurations.length > 0 ? initialDurations : [60],
  );
  const [defaultDuration, setDefaultDuration] = useState(initialDefault);
  const [customDuration, setCustomDuration] = useState("");
  const currency = activeVariants[0]?.currency ?? "EUR";
  const rateCents =
    priceMode === "free" || hourlyRate.trim() === "" ? null : Math.round(Number(hourlyRate) * 100);
  const mixedRates = hasMixedHourlyRates(activeVariants, initialRateCents);
  const visibleDurations = uniqueDurations([...DURATION_PRESETS, ...selectedDurations]);

  function toggleDuration(duration: number) {
    setSelectedDurations((current) => {
      if (current.includes(duration)) {
        if (current.length === 1) return current;
        const next = current.filter((value) => value !== duration);
        if (defaultDuration === duration) setDefaultDuration(next[0] ?? 60);
        return next;
      }
      return uniqueDurations([...current, duration]);
    });
  }

  function addCustomDuration() {
    const duration = Number(customDuration);
    if (!Number.isInteger(duration) || duration < 15 || duration > 480 || duration % 15 !== 0)
      return;
    setSelectedDurations((current) => uniqueDurations([...current, duration]));
    setCustomDuration("");
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await run(
      () =>
        saveWorkspaceHourlyPricing({
          offerId: offer.id,
          hourlyRateCents: priceMode === "free" ? null : rateCents,
          durationMinutes: selectedDurations,
          defaultDurationMinutes: selectedDurations.includes(defaultDuration)
            ? defaultDuration
            : (selectedDurations[0] ?? 60),
          currency,
        }),
      "Stundensatz und buchbare Dauern wurden gemeinsam gespeichert.",
    );
  }

  return (
    <form className="workspace-pricing-form" onSubmit={save}>
      <div className="workspace-section-heading">
        <div>
          <p className="workspace-kicker">Dauer & Preis</p>
          <h3>Einmal einstellen</h3>
        </div>
      </div>
      <p className="workspace-pricing-intro">
        Du pflegst nur den Stundensatz. Alle Preise der ausgewählten Dauern werden automatisch
        berechnet und gemeinsam gespeichert.
      </p>

      <div className="workspace-price-mode" role="radiogroup" aria-label="Preisart">
        <label className={priceMode === "hourly" ? "is-selected" : ""}>
          <input
            type="radio"
            name={`price-mode-${offer.id}`}
            value="hourly"
            checked={priceMode === "hourly"}
            onChange={() => setPriceMode("hourly")}
          />
          <span>
            <strong>Stundensatz</strong>
            <small>Preise automatisch berechnen</small>
          </span>
        </label>
        <label className={priceMode === "free" ? "is-selected" : ""}>
          <input
            type="radio"
            name={`price-mode-${offer.id}`}
            value="free"
            checked={priceMode === "free"}
            onChange={() => setPriceMode("free")}
          />
          <span>
            <strong>Kostenfrei</strong>
            <small>Alle gewählten Dauern ohne Preis</small>
          </span>
        </label>
      </div>

      {priceMode === "hourly" ? (
        <label className="workspace-hourly-rate">
          Stundensatz in €
          <div>
            <input
              type="number"
              min="0"
              step="0.01"
              value={hourlyRate}
              onChange={(event) => setHourlyRate(event.target.value)}
              placeholder="z. B. 180"
              required
            />
            <span>pro 60 Minuten</span>
          </div>
        </label>
      ) : null}

      {mixedRates ? (
        <p className="workspace-pricing-note">
          Die bisherigen Einzelpreise folgen noch unterschiedlichen Stundenwerten. Beim Speichern
          werden sie sauber auf diesen einen Stundensatz vereinheitlicht.
        </p>
      ) : null}

      <fieldset className="workspace-duration-fieldset">
        <legend>Buchbare Dauern</legend>
        <div className="workspace-duration-grid">
          {visibleDurations.map((duration) => {
            const selected = selectedDurations.includes(duration);
            return (
              <label key={duration} className={selected ? "is-selected" : ""}>
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => toggleDuration(duration)}
                />
                <span>
                  <strong>{formatDuration(duration)}</strong>
                  <small>{formatCalculatedPrice(rateCents, duration, currency)}</small>
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      <div className="workspace-custom-duration">
        <label>
          Weitere Dauer
          <input
            type="number"
            min="15"
            max="480"
            step="15"
            value={customDuration}
            onChange={(event) => setCustomDuration(event.target.value)}
            placeholder="Minuten"
          />
        </label>
        <button type="button" onClick={addCustomDuration}>
          Hinzufügen
        </button>
      </div>

      <label className="workspace-default-duration">
        Vorausgewählte Dauer
        <select
          value={defaultDuration}
          onChange={(event) => setDefaultDuration(Number(event.target.value))}
        >
          {selectedDurations.map((duration) => (
            <option key={duration} value={duration}>
              {formatDuration(duration)} · {formatCalculatedPrice(rateCents, duration, currency)}
            </option>
          ))}
        </select>
      </label>

      <button
        className="workspace-primary"
        type="submit"
        disabled={busy || selectedDurations.length === 0}
      >
        Stundensatz & Dauern speichern
      </button>
    </form>
  );
}

function deriveHourlyRateCents(variants: WorkspaceVariant[]): number | null {
  const reference =
    variants.find((variant) => variant.isDefault && variant.priceCents !== null) ??
    variants.find((variant) => variant.priceCents !== null);
  if (!reference?.priceCents || reference.durationMinutes <= 0) return null;
  return Math.round((reference.priceCents * 60) / reference.durationMinutes);
}

function hasMixedHourlyRates(variants: WorkspaceVariant[], referenceRate: number | null): boolean {
  if (referenceRate === null) return false;
  return variants.some(
    (variant) =>
      variant.priceCents !== null &&
      Math.abs(Math.round((variant.priceCents * 60) / variant.durationMinutes) - referenceRate) > 1,
  );
}

function uniqueDurations(values: number[]): number[] {
  return [...new Set(values)].sort((left, right) => left - right);
}

function formatDuration(duration: number): string {
  if (duration < 60) return `${duration} Minuten`;
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;
  if (minutes === 0) return hours === 1 ? "1 Stunde" : `${hours} Stunden`;
  return `${hours} Std. ${minutes} Min.`;
}

function formatCalculatedPrice(
  hourlyRateCents: number | null,
  duration: number,
  currency: string,
): string {
  if (hourlyRateCents === null) return "Kostenfrei";
  const price = Math.round((hourlyRateCents * duration) / 60);
  return new Intl.NumberFormat("de-DE", { style: "currency", currency }).format(price / 100);
}

function LocationOverview({ snapshot }: { snapshot: WorkspaceSnapshot }) {
  return (
    <section className="workspace-panel">
      <p className="workspace-kicker">Orte & Online</p>
      <h2>Buchungsorte</h2>
      <div className="workspace-summary-list">
        {snapshot.locations.map((location) => (
          <article key={location.id}>
            <strong>{location.mode === "online" ? "Online · Google Meet" : location.name}</strong>
            <span>{location.isActive ? "Aktiv" : "Deaktiviert"}</span>
          </article>
        ))}
      </div>
      <a className="workspace-primary-link" href="/?admin=legacy">
        Orte bearbeiten
      </a>
    </section>
  );
}

function AvailabilityOverview({ snapshot }: { snapshot: WorkspaceSnapshot }) {
  return (
    <section className="workspace-panel">
      <p className="workspace-kicker">Verfügbarkeit</p>
      <h2>Zeitfenster</h2>
      <p>
        {snapshot.windows.filter((window) => window.isActive).length} aktive Zeitfenster ·{" "}
        {snapshot.windows.reduce((sum, window) => sum + window.openSlotCount, 0)} offene Slots
      </p>
      <div className="workspace-summary-list">
        {snapshot.windows.slice(0, 8).map((window) => (
          <article key={window.id}>
            <strong>{new Date(window.startsAt).toLocaleDateString("de-DE")}</strong>
            <span>{window.openSlotCount} freie Slots</span>
          </article>
        ))}
      </div>
      <a className="workspace-primary-link" href="/?admin=legacy">
        Zeitfenster bearbeiten
      </a>
    </section>
  );
}

function HistoryOverview({ snapshot }: { snapshot: WorkspaceSnapshot }) {
  return (
    <section className="workspace-panel">
      <p className="workspace-kicker">Änderungsverlauf</p>
      <h2>Letzte Änderungen</h2>
      <div className="workspace-history">
        {snapshot.recentChanges.map((change) => (
          <article key={change.id}>
            <div>
              <strong>{labelAction(change.action)}</strong>
              <p>{change.actor}</p>
            </div>
            <time>{new Date(change.createdAt).toLocaleString("de-DE")}</time>
          </article>
        ))}
      </div>
    </section>
  );
}

function labelAction(action: string) {
  if (action === "save_offer") return "Angebot geändert";
  if (action === "save_offer_variant") return "Preis oder Dauer geändert";
  if (action === "save_hourly_pricing") return "Stundensatz oder Dauer geändert";
  if (action === "save_location") return "Ort geändert";
  return action.replaceAll("_", " ");
}

function toMessage(reason: unknown) {
  return reason instanceof Error ? reason.message : "Es ist ein unbekannter Fehler aufgetreten.";
}
